import type { ForecastMetaDataType } from "../../../hooks/useFetchForecastMetaData";
import { periodKeyToChartDate } from "../../../utils/chartDateUtils";
import { isPercentageMetric } from "../../../utils/percentageMetrics";
import { formatProducer } from "../../cubeDataTable/columnDefinitions/shared/producerLabel";
import {
  AXIS_YKEY,
  CATEGORY_TO_AXIS,
  INVENTORY_BLOCKS,
  type KpiAxisCategory
} from "./graphConstants";

/** Result of classifying a metric by its axis, inventory flag and additivity. */
interface MetricClassification {
  axisCategory: KpiAxisCategory;
  isInventory: boolean;
  /**
   * The metric's periods add up (`temporal_agg = sum` in the seed, shipped as
   * `metricTemporalAgg`), so a running sum over them is a period-to-date
   * total. A mean, first or last rule — a rate, a price, a stock level — does
   * not add up.
   */
  isAdditive: boolean;
}

/** A single unpivoted chart data point with a fiscal period key, its chart date, and one or more y-values. */
type DataPoint = {
  period: string;
  chartDate: Date;
} & Record<string, string | number | Date>;

/**
 * Classify a metric into its axis category, inventory flag and additivity.
 * Returns null for a metric whose category has no axis.
 *
 * A percentage metric goes to the percent axis whatever its category says.
 * The seed categorises margin % as `Value` — the category drives aggregation
 * and the summary's view filter, not display — so a percent `fmt_str` decides
 * the axis: a 0..1 fraction on the value axis flattens to zero beside millions.
 */
export function classifyMetric(
  metricName: string,
  metaData: ForecastMetaDataType
): MetricClassification | null {
  const rawCategory = metaData.metricCategoryMapping[metricName];
  const axisCategory = isPercentageMetric(
    metricName,
    metaData.metricFormatMapping
  )
    ? "PERCENTAGE"
    : rawCategory
      ? CATEGORY_TO_AXIS[rawCategory]
      : undefined;

  if (!axisCategory) return null;

  const block = metaData.metricBlockMapping[metricName];
  return {
    axisCategory,
    isInventory: block ? INVENTORY_BLOCKS.has(block) : false,
    isAdditive: metaData.metricTemporalAgg[metricName] === "sum"
  };
}

/**
 * Get the year offset for a fiscal year from metadata.
 */
export function getYearOffset(
  fiscalYearName: string,
  metaData: ForecastMetaDataType
): number {
  return metaData.yearMapping[fiscalYearName] ?? 0;
}

/**
 * Unpivot a grid row into an array of data points for charting.
 * Each point has { period, [yKey]: value } where yKey is the axis-specific key.
 * Null/undefined values are filtered out.
 */
export function unpivotRow(
  row: Record<string, unknown>,
  timePeriodKeys: string[],
  axisCategory: KpiAxisCategory
): DataPoint[] {
  const yKey = AXIS_YKEY[axisCategory];
  const points: DataPoint[] = [];

  for (const key of timePeriodKeys) {
    const rawValue = row[key];
    if (rawValue == null) continue;
    const value = Number(rawValue);
    if (Number.isNaN(value)) continue;
    const chartDate = periodKeyToChartDate(key);
    points.push({ period: key, chartDate, [yKey]: value });
  }

  return points;
}

/**
 * Running-sum the data points when `accumulated` is on and the metric is
 * additive. The gate is the seed's `temporal_agg` rule (`isAdditive`); the
 * percentage and inventory exclusions predate it and only ever remove
 * accumulation, so they stay.
 */
export function applyCumulativeSum(
  data: DataPoint[],
  accumulated: boolean,
  classification: MetricClassification
): DataPoint[] {
  if (
    !accumulated ||
    !classification.isAdditive ||
    classification.axisCategory === "PERCENTAGE" ||
    classification.isInventory
  ) {
    return data;
  }

  const yKey = AXIS_YKEY[classification.axisCategory];
  let cumSum = 0;
  return data.map((point) => {
    cumSum += (point[yKey] as number) ?? 0;
    return { ...point, [yKey]: cumSum };
  });
}

/**
 * Build a human-readable trace label from a grid row.
 * Format: "Display Name · FY · Producer"
 */
export function getTraceLabel(
  row: Record<string, unknown>,
  metaData: ForecastMetaDataType
): string {
  const metricName = String(row.metric_name ?? "");
  const displayName = metaData.metricDisplayMapping[metricName] ?? metricName;
  const fiscalYear = String(row.fiscal_year_name ?? "");
  const producer = formatProducer(String(row.producer_type ?? ""));
  return `${displayName} · ${fiscalYear} · ${producer}`;
}
